import Joi from "joi/lib/index.js";
import _ from "lodash";

import {
  MONTHLY_PRICE_LABEL,
  ANNUAL_PRICE_LABEL,
  nameSchema,
} from './constants';

import {trimBlankPrices} from "./methods";

/**
 * Used to generate the rules for a single price input.
 */
export function singlePriceSchema(
  label,
  minimumPrice,
  required,
) {
  let basicSchema = Joi.number().greater(0).label(label);

  /**
   * Extend the basic validation to include a required check, if this has been deemed necessary.
   * The required flag is currently only calculated from the currencyInputRequired() method.
   */
  if (required) {
    basicSchema = basicSchema.required();
  }

  return basicSchema.min(minimumPrice).messages({
    'number.min': 'This price is below the minimum amount.',
  });
}

/**
 * Used to determine if ALL prices are empty
 */
export function allPricesAreEmpty(enabledCurrencies, customerPrices) {
  let allPricesEmpty = true;

  enabledCurrencies.forEach((currencyCode) => {
    if (
      customerPrices[currencyCode] &&
      (
        customerPrices[currencyCode].monthlyRaw > 0 ||
        customerPrices[currencyCode].annualRaw > 0
      )
    ) {
      allPricesEmpty = false;
    }
  });

  return allPricesEmpty;
}

/**
 * Used to determine if a given currency input on softwareProduct is required.
 * Checks the relevant billing period across other enabled currencies and checks the opposing billing period.
 */
export function isCurrencyInputRequired(
  supportedCurrencies,
  userDefinedPrices,
  currencyCode,
  period,
) {
  let isRequired = false;

  const otherPeriods = {
    'monthlyRaw': 'annualRaw',
    'annualRaw': 'monthlyRaw',
  };

  let enabledCurrencies = Object.entries(supportedCurrencies)
    .map(([currencyCode, {enabled}]) => {
      return enabled ? currencyCode : null;
    })
    .filter((item) => item);

  let anyOtherCurrencyEnabled = false;

  enabledCurrencies
    .filter((otherCurrencyCode) => otherCurrencyCode !== currencyCode)
    .forEach((otherCurrencyCode) => {
      if (userDefinedPrices[otherCurrencyCode][period]) {
        anyOtherCurrencyEnabled = true;
      }
    });

  if (enabledCurrencies.includes(currencyCode)) {
    isRequired = true;

    const otherPeriodKey = otherPeriods[period];
    if (userDefinedPrices[currencyCode][otherPeriodKey] > 0) {
      isRequired = false;
    }

    if (userDefinedPrices[currencyCode][period] > 0) {
      isRequired = true;
    }

    if (!anyOtherCurrencyEnabled) {
      isRequired = false;
    }
  }

  return isRequired || allPricesAreEmpty(enabledCurrencies, userDefinedPrices);
}

/**
 * The overall validation schema for the prices array on softwareProduct.
 * Due to the layered conditions, the individual schemas have been abstracted to dedicated methods.
 * This allows for easy extension of the price validation (e.g. to include new currencies).
 */
export function allPricesSchema(
  resalePrices,
  userDefinedPrices,
  supportedCurrencies,
) {
  const currencySchemas = {};

  const validCurrencies = Object.keys(supportedCurrencies);

  validCurrencies.forEach((currencyCode) => {
    currencySchemas[currencyCode] = {
      monthlyRaw: Joi.any(),
      annualRaw: Joi.any(),
    };

    const monthlyMinimum = resalePrices[currencyCode].MONTH.minimum;
    const annualMinimum = resalePrices[currencyCode].YEAR.minimum;

    const isMonthlyRequired = isCurrencyInputRequired(
      supportedCurrencies,
      userDefinedPrices,
      currencyCode,
      'monthlyRaw',
    );
    if (isMonthlyRequired) {
      currencySchemas[currencyCode].monthlyRaw =
        singlePriceSchema(MONTHLY_PRICE_LABEL, monthlyMinimum, isMonthlyRequired);
    }

    const isAnnualRequired = isCurrencyInputRequired(
      supportedCurrencies,
      userDefinedPrices,
      currencyCode,
      'annualRaw',
    );
    if (isAnnualRequired) {
      currencySchemas[currencyCode].annualRaw =
        singlePriceSchema(ANNUAL_PRICE_LABEL, annualMinimum, isAnnualRequired);
    }
  });

  return Joi.object()
    .keys(currencySchemas)
    .min(1)
    .messages({
      'object.min': 'Please enable at least one price',
    });
}

/**
 * Used to validate the user-created software product.
 */
export function validateSoftwareProduct(state, {softwareProduct}) {
  const {prices: priceInputs} = softwareProduct;
  const {softwarePrices: resalePrices, supportedCurrencies} = state;

  const schema = Joi.object({
    name: nameSchema,
    supportedCurrencies: Joi.array()
      .min(1)
      .required()
      .messages({
        'any.required': `Please enable at least one currency.`,
        'array.min': `Please enable at least one currency.`,
      }),
    prices: allPricesSchema(
      resalePrices, // Contains minimums
      priceInputs,
      supportedCurrencies,
    ),
  })
  .messages({
    'object.base': `Please enter a monthly or annual price.`,
    'object.missing': `Please enter a monthly or annual price.`,
    'number.integer': `Please enter a valid price.`,
    'number.min': 'This price is below the minimum amount.',
    'number.base': 'Please enter a valid price',
  })
  .unknown(true);

  let enabledCurrencies = Object.entries(supportedCurrencies)
    .map(([currencyCode, data]) => {
      return data.enabled ? currencyCode : false;
    });

  /**
   * Tweaking the product to only validate name and price.
   * Prices are tweaked on the fly, see: trimBlankPrices() method for more info.
   */
  const trimmedProduct = {
    name: softwareProduct.name,
    prices: priceInputs,//trimBlankPrices(priceInputs, enabledCurrencies),
    supportedCurrencies: Object.entries(supportedCurrencies)
      .map(([currencyCode]) => currencyCode),
  };

  console.log('validating', trimmedProduct);

  const {error: errors} = schema.validate(
    trimmedProduct,
    { abortEarly: false }
  );
  return errors;
}
